import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Spacing, Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import { ReactComponent as CalendarIcon } from 'img/icons/subscriptions/calendar-icon.svg';
import { ReactComponent as CardIcon } from 'img/icons/subscriptions/card-icon.svg';
import { ReactComponent as InfoIcon } from 'img/icons/subscriptions/info-icon.svg';
import React, { type VFC } from 'react';
import { useSelector } from 'react-redux';
import { BillingDataPlatform } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { BASEQUOTA, IS_SOCIAL_USER } from 'reactApp/appHelpers/configHelpers';
/* tempexp_20958-next-line */
import { cardUnlinkOutflowTouch } from 'reactApp/appHelpers/featuresHelpers/features/cardUnlinkOutflow';
import { isDiscountOutflowOldProduct } from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import { MultiSessionDeviceSubs } from 'reactApp/appHelpers/featuresHelpers/features/multiSessionSubscriptions';
import { abPauseSubscription } from 'reactApp/appHelpers/featuresHelpers/features/pauseSubscription';
import { TBankMobileBlock } from 'reactApp/components/TBankMobileBlock/TBankMobileBlock';
import { PARTNER_TITLE } from 'reactApp/modules/billingData/billing.data';
import {
    type BillingDataSubscriptionInfo,
    BillingDataPromoSubscriptionType,
    BillingDataSubscriptionInfoStatus,
    isImapSubscription,
    isTBankSubscription,
    isVkMusicReward,
} from 'reactApp/modules/billingData/billingData.types';
import { BuySubscriptionSelectors } from 'reactApp/modules/buySubscription/buySubscription.module';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { cheapestProductIDs } from 'reactApp/modules/products/products.const';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import { getPausedSubscriptionStorageKey, isPausedSubscription } from 'reactApp/modules/subscriptions/subscriptions.helpers';
import { UserStorageSelectors } from 'reactApp/modules/user/userStorage';
import {
    isFamilyPlaceByMemberSubscription,
    isGraceSubscription,
    isVkMusicSubscription,
} from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import { PersonalDiscountBanner } from 'reactApp/sections/SubscriptionsPage/ui/PersonalDiscountPrice/PersonalDiscountPrice';
import type { RootState } from 'reactApp/store';
import type { Product } from 'reactApp/types/Billing';
import { GraceSubscription } from 'reactApp/ui/GraceSubscription/GraceSubscription';
import { VkMusicBlock } from 'reactApp/ui/Mobile/SubscriptionInfoModal/Components/VkMusicBlock/VkMusicBlock';
import { getTypeOfPeriodName } from 'reactApp/utils/Period';
import { computersGenitive } from 'reactApp/utils/pluralHelpers';

import { CommonInfo } from '../CommonInfo/CommonInfo';
import { SubscriptionInfoItem } from '../SubscriptionInfoItem/SubscriptionInfoItem';
import { useHandlers } from './hooks/useHandlers';
import styles from './ModernSubscription.css';
import { getCardNumber, getExpiresTitle, getNextPayment, getTariffConditionsText } from './ModernSubscription.helpers';

type Props = IPropsWithPopup<{ subscription: BillingDataSubscriptionInfo; isRebrandingQuotaLanding?: boolean; isRebranding?: boolean }>;

export const ModernSubscription: VFC<Props> = ({ subscription, onClose, isRebrandingQuotaLanding, isRebranding = false }) => {
    const vkMusicSubscription = isVkMusicSubscription(subscription);
    const vkMusicReward = isVkMusicReward(subscription);
    const TBankSubscription = isTBankSubscription(subscription);
    /* tempexp_20582-next-line */
    const isImapSub = isImapSubscription(subscription);
    const showDefaultHeader = !vkMusicSubscription && !TBankSubscription && !isImapSub;
    const status = subscription.status as BillingDataSubscriptionInfoStatus;
    const { expires, type, renewAt, productId, autorenewal, partner, space, accountLink } = subscription;
    const { card } = useSelector(CardSelectors.getCard);
    const cardNumber = getCardNumber(card);
    const paymentMethod = card?.isSbp ? 'Система быстрых платежей' : cardNumber || '';
    const pausedSubscription: boolean =
        useSelector((state: RootState) => UserStorageSelectors.get(state, getPausedSubscriptionStorageKey(subscription.id))) &&
        isPausedSubscription(abPauseSubscription, subscription.renewAt);
    const nextPayment = getNextPayment(renewAt, expires, pausedSubscription);
    // TODO: если id невалидный, то product будет undefined и всё посыпется. Стоит предусмотреть и убрать as Product
    const product = useSelector((state: RootState) => ProductsSelectors.getProductById(state, productId)) as Product;
    const conditions = getTariffConditionsText(subscription, product);

    const isMobileOsType = [BillingDataPlatform.ios, BillingDataPlatform.android, BillingDataPlatform.rustore].includes(type);
    const showCard = !isMobileOsType;
    // пока скрываем для мобильных подписок, тк плашка отображается некорректно
    const showMainInfo = type === BillingDataPlatform.web || vkMusicSubscription || TBankSubscription;
    const showNextPayment = (typeof autorenewal === 'boolean' || vkMusicSubscription || TBankSubscription) && showCard;
    const showExpires = showDefaultHeader && expires && (partner || status === BillingDataSubscriptionInfoStatus.SUSPENDED);

    const {
        handleUpdateCard,
        handleCancelNextPayment,
        handleGoToSubscription,
        handleRenewNextPayment,
        handlePartnerClick,
        handleRemoveCard,
    } = useHandlers(subscription, onClose);

    const subscriptionType = subscription.type as BillingDataPlatform | BillingDataPromoSubscriptionType;

    // tempexp_16598-start
    const isCheapest = productId && Object.values(cheapestProductIDs).includes(productId);
    const tariffSpace = isCheapest ? bytesToNDigits(BASEQUOTA, 3) : space;
    const customCheapestTitle = isCheapest ? `${bytesToNDigits(BASEQUOTA, 3).value} без рекламы` : undefined;
    // tempexp_16598-end

    let discountBanner;
    let discountInOutflowConditions;
    const canSetDiscountOutflow = useSelector(BuySubscriptionSelectors.getCanSetDiscountInOutflow);
    const discountOutflowPercent = useSelector(BuySubscriptionSelectors.getDiscountInOutflowPercent);
    const discountOutflowPrice = useSelector(BuySubscriptionSelectors.getDiscountInOutflow);
    const isOldFlowDiscountAbProduct = isDiscountOutflowOldProduct(productId);
    // поддержка старого флоу экспа скидка в оттоке
    if (isOldFlowDiscountAbProduct) {
        discountBanner = <PersonalDiscountBanner />;
        discountInOutflowConditions = conditions;
    }
    if (!canSetDiscountOutflow && discountOutflowPrice && discountOutflowPercent) {
        discountBanner = <PersonalDiscountBanner />;
        const discountConditions = `${discountOutflowPrice} ₽ в ${getTypeOfPeriodName(product?.period)}`;
        discountInOutflowConditions = product.price ? (
            <div className={styles.discountPriceContainer}>
                <span className={styles.crossedText}>{product.price} ₽</span>
                <span>{discountConditions}</span>
            </div>
        ) : (
            discountConditions
        );
    }

    // tempexp_17360-next-line
    let giftLabel;
    if (product?.discountPeriod === '13m') {
        giftLabel = '30 дней';
    }

    if (product?.discountPeriod === '15m') {
        giftLabel = '3 месяца';
    }

    const isGrace = isGraceSubscription(subscription);

    /* tempexp_20439-start */
    const isMultiSessionSubsEnabled = !!product?.id && MultiSessionDeviceSubs.enabled;
    const isMultiSessionFreeChoice = !!product?.id && isMultiSessionSubsEnabled && MultiSessionDeviceSubs.isFreeChoiceProduct(product.id);
    const isMultiSessionPresetInSubs =
        !!product?.id && isMultiSessionSubsEnabled && MultiSessionDeviceSubs.isPresetInSubsProduct(product.id);
    const devices = product?.id ? MultiSessionDeviceSubs.getDeviceCount(product.id) : 0;
    let customMultiSessionTitle;
    if (isMultiSessionFreeChoice) {
        customMultiSessionTitle = `Вход в Почту с ${devices} ${computersGenitive(devices)}`;
    }
    if (isMultiSessionPresetInSubs) {
        customMultiSessionTitle = `Mail\u00A0Space Для\u00A0работы и\u00A0вход в\u00A0Почту с\u00A0${devices}\u00A0${computersGenitive(
            devices
        )}`;
    }
    /* tempexp_20439-end */
    return (
        <>
            {isImapSub && (
                <>
                    <Text className={styles.customTitle}>Создание паролей</Text>
                    <Spacing size={4} />
                    <Text className={styles.active}>Подписка активна</Text>
                </>
            )}
            {showDefaultHeader && (
                <CommonInfo
                    space={tariffSpace}
                    isDeviceFreeSubs={isMultiSessionSubsEnabled && isMultiSessionFreeChoice}
                    isDevicePresetSubs={isMultiSessionSubsEnabled && isMultiSessionPresetInSubs}
                    status={status as BillingDataSubscriptionInfoStatus}
                    customTitle={customMultiSessionTitle || customCheapestTitle}
                    isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                    isGrace={isGrace}
                />
            )}

            {showExpires && (
                <span className={classNames(styles.expires, { [styles.expires_warning]: status === 'SUSPENDED' })}>
                    {getExpiresTitle(expires, status)}
                </span>
            )}
            {showDefaultHeader && subscriptionType === BillingDataPromoSubscriptionType.quota && (
                <span
                    className={classNames(styles.tariffManagement, {
                        [styles.tariffManagement_rebrandingQuotaLanding]: isRebrandingQuotaLanding,
                    })}
                >
                    {isFamilyPlaceByMemberSubscription(subscription) ? 'Это семейная подписка' : 'Это подарочная подписка'}
                </span>
            )}
            {showDefaultHeader &&
                partner &&
                (accountLink ? (
                    <div
                        className={classNames(styles.tariffManagement, {
                            [styles.tariffManagement_rebrandingQuotaLanding]: isRebrandingQuotaLanding,
                        })}
                    >
                        Управление тарифом доступно в личном кабинете&nbsp;
                        <a className={styles.link} onClick={handlePartnerClick} target="_blank" href={accountLink} rel="noreferrer">
                            {PARTNER_TITLE[partner]}
                        </a>
                    </div>
                ) : null)}
            {showMainInfo && (
                <div className={styles.mainInfo}>
                    {isGrace && <GraceSubscription isMobile subscription={subscription} product={product} />}
                    {vkMusicSubscription && (
                        <VkMusicBlock accountLink={subscription.accountLink} isReward={vkMusicReward} product={product} space={space} />
                    )}
                    {TBankSubscription && <TBankMobileBlock space={space} />}
                    <SubscriptionInfoItem
                        title="Условия подписки"
                        subtitle={discountInOutflowConditions || conditions}
                        discountBanner={discountBanner}
                        buttonText="Добавить место"
                        Icon={InfoIcon}
                        onClick={handleGoToSubscription}
                        isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                        giftLabel={giftLabel}
                        showButton={!vkMusicReward && !TBankSubscription}
                        heightByContent={vkMusicReward || TBankSubscription}
                    />
                    {showNextPayment && (
                        <SubscriptionInfoItem
                            title={autorenewal ? 'Следующее списание' : 'Дата окончания'}
                            subtitle={nextPayment}
                            buttonText={autorenewal ? 'Отключить' : 'Восстановить'}
                            Icon={CalendarIcon}
                            onClick={
                                autorenewal ? () => handleCancelNextPayment(isRebrandingQuotaLanding, isRebranding) : handleRenewNextPayment
                            }
                            isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                            showButton={!vkMusicReward && !TBankSubscription && (autorenewal || !IS_SOCIAL_USER)}
                            heightByContent={vkMusicReward || TBankSubscription}
                        />
                    )}
                    {showCard && (
                        <SubscriptionInfoItem
                            title="Способ оплаты"
                            subtitle={
                                cardNumber !== null ? paymentMethod : TBankSubscription ? 'Система быстрых платежей' : 'Карта не привязана'
                            }
                            buttonText={cardNumber !== null ? `Изменить${card?.isSbp ? '' : ' карту'}` : 'Привязать карту'}
                            Icon={CardIcon}
                            onClick={handleUpdateCard}
                            isRebrandingQuotaLanding={isRebrandingQuotaLanding}
                            showButton={!vkMusicReward && !TBankSubscription}
                            heightByContent={vkMusicReward || TBankSubscription}
                            /* tempexp_20958-start */
                            secondaryButtonText={
                                cardUnlinkOutflowTouch && cardNumber !== null && !vkMusicReward && !TBankSubscription
                                    ? 'Удалить'
                                    : undefined
                            }
                            onSecondaryButtonClick={handleRemoveCard}
                            /* tempexp_20958-end */
                        />
                    )}
                </div>
            )}
        </>
    );
};
